export interface ShareImageStats {
  correct: number
  wrong: number
  accuracy: number
  wordsLearnedToday: number
  streakDays?: number
}

export interface ShareImageLabels {
  brand: string
  heading: string
  correctLabel: string
  wrongLabel: string
  accuracyLabel: string
  newWordsTodayLabel: string
  streakLabel?: string
  footerUrl: string
}

const WIDTH = 1080
const HEIGHT = 1350

// Fixed brand palette mirroring the light-theme tokens in src/index.css. Canvas cannot
// read CSS custom properties off-screen, and the shared image must look identical
// regardless of the sharer's local theme, so these are pinned constants rather than
// looked up at render time.
const COLORS = {
  bgPage: '#F8F9FA',
  bgSurface: '#FFFFFF',
  textPrimary: '#2D3436',
  textSecondary: '#636E72',
  accent: '#4A6D8C',
  accentSoft: 'rgba(74, 109, 140, 0.12)',
  success: '#27AE60',
  danger: '#B93426',
  border: '#DEE2E6',
}

// Matches --font-ui in index.css, minus 'Inter' (not bundled — canvas can only draw
// fonts actually installed/loaded, so we rely on the same system fallback chain the
// app already falls back to when Inter is unavailable).
const FONT_STACK = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"

function font(weight: number, size: number) {
  return `${weight} ${size}px ${FONT_STACK}`
}

let fontsWarmed = false

/**
 * Pays Chromium's one-time canvas-text cold-start cost (~1-2s: resolving/rasterizing the
 * whole FONT_STACK fallback chain on the first-ever fillText call in the page) up front,
 * with a throwaway 4x4 canvas — measured to be size- and canvas-independent, so a tiny
 * draw here fully warms later full-resolution draws down to a few ms. Call this as early
 * as possible (milestone detection), well before the user can reach the share sheet, so
 * the sheet's own renders never pay this cost. Idempotent and safe to call repeatedly.
 */
export function warmShareImageFonts(): void {
  if (fontsWarmed) return
  fontsWarmed = true
  const canvas = document.createElement('canvas')
  canvas.width = 4
  canvas.height = 4
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.font = font(800, 16)
  ctx.fillText('W', 0, 4)
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

/** Renders a branded, self-contained PNG summarizing session/day stats for sharing. */
export async function renderShareImage(stats: ShareImageStats, labels: ShareImageLabels): Promise<Blob> {
  const canvas = document.createElement('canvas')
  canvas.width = WIDTH
  canvas.height = HEIGHT
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')

  ctx.fillStyle = COLORS.bgPage
  ctx.fillRect(0, 0, WIDTH, HEIGHT)
  ctx.textBaseline = 'alphabetic'

  // Brand mark: a small two-card glyph + wordmark, top-left.
  const markX = 80
  const markY = 96
  ctx.fillStyle = COLORS.accentSoft
  roundRect(ctx, markX, markY, 44, 56, 10)
  ctx.fill()
  ctx.fillStyle = COLORS.accent
  roundRect(ctx, markX + 14, markY + 10, 44, 56, 10)
  ctx.fill()
  ctx.fillStyle = COLORS.textPrimary
  ctx.font = font(700, 34)
  ctx.textAlign = 'left'
  ctx.fillText(labels.brand, markX + 74, markY + 42)

  // Heading
  ctx.fillStyle = COLORS.textPrimary
  ctx.font = font(700, 56)
  wrapText(ctx, labels.heading, 80, 260, WIDTH - 160, 64)

  // Stats card
  const cardX = 80
  const cardY = 400
  const cardW = WIDTH - 160
  const cardH = 300
  ctx.fillStyle = COLORS.bgSurface
  roundRect(ctx, cardX, cardY, cardW, cardH, 32)
  ctx.fill()
  ctx.strokeStyle = COLORS.border
  ctx.lineWidth = 2
  roundRect(ctx, cardX, cardY, cardW, cardH, 32)
  ctx.stroke()

  const cols = [
    { value: String(stats.correct), label: labels.correctLabel, color: COLORS.success },
    { value: String(stats.wrong), label: labels.wrongLabel, color: COLORS.danger },
    { value: `${stats.accuracy}%`, label: labels.accuracyLabel, color: COLORS.accent },
  ]
  const colW = cardW / 3
  cols.forEach((col, i) => {
    const cx = cardX + colW * i + colW / 2
    ctx.textAlign = 'center'
    ctx.fillStyle = col.color
    ctx.font = font(700, 84)
    ctx.fillText(col.value, cx, cardY + 150)
    ctx.fillStyle = COLORS.textSecondary
    ctx.font = font(500, 30)
    ctx.fillText(col.label, cx, cardY + 210)
    if (i > 0) {
      ctx.strokeStyle = COLORS.border
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(cardX + colW * i, cardY + 60)
      ctx.lineTo(cardX + colW * i, cardY + cardH - 60)
      ctx.stroke()
    }
  })

  // New words today + streak row
  let rowY = cardY + cardH + 110
  ctx.textAlign = 'left'
  ctx.font = font(500, 36)
  ctx.fillStyle = COLORS.textSecondary
  ctx.fillText(labels.newWordsTodayLabel, 80, rowY)
  const prefixWidth = ctx.measureText(labels.newWordsTodayLabel + ' ').width
  ctx.font = font(700, 36)
  ctx.fillStyle = COLORS.textPrimary
  ctx.fillText(String(stats.wordsLearnedToday), 80 + prefixWidth, rowY)

  if (labels.streakLabel) {
    rowY += 64
    ctx.font = font(600, 36)
    ctx.fillStyle = COLORS.accent
    ctx.fillText(labels.streakLabel, 80, rowY)
  }

  // Footer: app URL
  ctx.textAlign = 'center'
  ctx.font = font(500, 30)
  ctx.fillStyle = COLORS.textSecondary
  ctx.fillText(labels.footerUrl, WIDTH / 2, HEIGHT - 80)

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('Failed to generate share image'))
    }, 'image/png')
  })
}

export type MilestoneShareField = 'light' | 'inv'

export interface MilestoneShareLabels {
  brand: string
  /** Localized tagline under the wordmark, e.g. "English vocabulary trainer". */
  category: string
  /** Uppercase eyebrow above the numeral, e.g. "Vocabulary milestone". */
  eyebrow: string
  /** Pre-formatted hero numeral, locale-correct group separators, e.g. "1,000" / "5 000". */
  numeral: string
  label: string
  /** Pre-formatted real total, e.g. actual words-known count (may differ from the numeral,
   *  which is the threshold) or the same streak-days figure for streak milestones. */
  totalValue: string
  totalSuffix: string
  /** Quiet footer line — the runtime origin, never a sentence that repeats `label`. */
  footer: string
}

/** Reads the app's current UI theme to pick a default field for the milestone card —
 *  light theme -> light field, dark theme -> inverted accent field (design's dark-field
 *  palette, not the app dark theme's surface colors). */
export function getCurrentMilestoneField(): MilestoneShareField {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'inv' : 'light'
}

// Direction C palette + colors per field (docs/design/milestone-share-card.html).
const MILESTONE_FIELD_COLORS: Record<MilestoneShareField, {
  bg: string; markBg: string; markInner: string; wordmark: string; category: string
  eyebrow: string; numeral: string; bracket: string; label: string
  pillBg: string; pillValue: string; pillSuffix: string; footer: string
}> = {
  light: {
    bg: '#F8F9FA', markBg: '#4A6D8C', markInner: '#F8F9FA', wordmark: '#2D3436', category: '#636E72',
    eyebrow: '#4A6D8C', numeral: '#4A6D8C', bracket: '#4A6D8C', label: '#2D3436',
    pillBg: 'rgba(74, 109, 140, 0.10)', pillValue: '#4A6D8C', pillSuffix: '#636E72', footer: '#AEB4B9',
  },
  inv: {
    bg: '#4A6D8C', markBg: '#F8F9FA', markInner: '#4A6D8C', wordmark: '#FFFFFF', category: '#CBD6DF',
    eyebrow: '#CDBB9A', numeral: '#FFFFFF', bracket: '#AEBFCC', label: '#FFFFFF',
    pillBg: 'rgba(255, 255, 255, 0.14)', pillValue: '#FFFFFF', pillSuffix: '#DDE4EA', footer: '#AEBFCC',
  },
}

// Static confetti — brand palette only, frozen. Positions/sizes/rotations/shapes copied
// verbatim from docs/design/milestone-share-card.html's PARTS table (Direction C):
// [x%, y%, size(px @1080 canvas), rotationDeg, shape(r=rect,c=circle), colorIndex]
const CONFETTI_PARTS: Array<[number, number, number, number, 'r' | 'c', number]> = [
  [10, 17, 26, 18, 'r', 0], [24, 21, 16, -24, 'c', 2], [38, 16, 22, 40, 'r', 3], [57, 19, 15, 12, 'c', 1],
  [72, 15, 24, -14, 'r', 2], [86, 20, 18, 32, 'r', 0], [92, 27, 14, -30, 'c', 3], [7, 26, 15, 50, 'c', 1],
  [15, 75, 20, -20, 'r', 3], [30, 79, 16, 28, 'c', 0], [44, 85, 24, -8, 'r', 1], [68, 82, 15, 36, 'c', 2],
  [78, 73, 22, 20, 'r', 0], [90, 79, 17, -26, 'r', 3], [9, 85, 14, 10, 'c', 2], [85, 86, 20, -16, 'r', 1],
]
const CONFETTI_PALETTE: Record<MilestoneShareField, string[]> = {
  light: ['#4A6D8C', '#6E92AE', '#AEBFCC', '#CDBB9A'],
  inv: ['#AEBFCC', '#6E92AE', '#CDBB9A', '#DDE4EA'],
}

function drawConfetti(ctx: CanvasRenderingContext2D, field: MilestoneShareField) {
  const palette = CONFETTI_PALETTE[field]
  ctx.save()
  ctx.globalAlpha = 0.9
  for (const [xPct, yPct, size, rotationDeg, shape, colorIndex] of CONFETTI_PARTS) {
    const cx = (xPct / 100) * WIDTH
    const cy = (yPct / 100) * HEIGHT
    const w = size
    const h = shape === 'r' ? size * 0.44 : size
    ctx.save()
    ctx.translate(cx, cy)
    ctx.rotate((rotationDeg * Math.PI) / 180)
    ctx.fillStyle = palette[colorIndex]
    if (shape === 'c') {
      ctx.beginPath()
      ctx.ellipse(0, 0, w / 2, h / 2, 0, 0, Math.PI * 2)
      ctx.fill()
    } else {
      roundRect(ctx, -w / 2, -h / 2, w, h, 2)
      ctx.fill()
    }
    ctx.restore()
  }
  ctx.restore()
}

/** Draws one L-shaped corner bracket of the numeral frame. */
function drawBracket(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  border: number,
  corner: 'tl' | 'tr' | 'bl' | 'br',
  color: string,
) {
  ctx.save()
  ctx.strokeStyle = color
  ctx.lineWidth = border
  const half = border / 2
  ctx.beginPath()
  if (corner === 'tl') {
    ctx.moveTo(x + half, y + size)
    ctx.lineTo(x + half, y + half)
    ctx.lineTo(x + size, y + half)
  } else if (corner === 'tr') {
    ctx.moveTo(x, y + half)
    ctx.lineTo(x + size - half, y + half)
    ctx.lineTo(x + size - half, y + size)
  } else if (corner === 'bl') {
    ctx.moveTo(x + half, y)
    ctx.lineTo(x + half, y + size - half)
    ctx.lineTo(x + size, y + size - half)
  } else {
    ctx.moveTo(x, y + size - half)
    ctx.lineTo(x + size - half, y + size - half)
    ctx.lineTo(x + size - half, y)
  }
  ctx.stroke()
  ctx.restore()
}

/** Shrinks the numeral's font size until it fits within maxWidth — handles both
 *  2-digit streaks and 5-digit vocabulary milestones without per-length constants. */
function fitNumeralFontSize(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): number {
  let size = 470
  while (size > 140) {
    ctx.font = font(800, size)
    if (ctx.measureText(text).width <= maxWidth) break
    size -= 10
  }
  return size
}

/** Renders the Direction C milestone share card (docs/design/milestone-share-card.html):
 *  brand block, centered eyebrow/numeral/label/total, frozen confetti, quiet URL footer.
 *  Used for both the preview sheet and the shared file — one render path for both. */
export async function renderMilestoneShareImage(
  labels: MilestoneShareLabels,
  field: MilestoneShareField = 'light',
): Promise<Blob> {
  const canvas = document.createElement('canvas')
  canvas.width = WIDTH
  canvas.height = HEIGHT
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')
  const c = MILESTONE_FIELD_COLORS[field]

  ctx.fillStyle = c.bg
  ctx.fillRect(0, 0, WIDTH, HEIGHT)
  drawConfetti(ctx, field)

  const PAD_X = 96
  const PAD_TOP = 104
  const PAD_BOTTOM = 92

  // Brand block — WHO
  const markSize = 64
  const brandTextBlockH = 42 + 9 + 25
  const brandRowH = Math.max(markSize, brandTextBlockH)
  const brandTop = PAD_TOP
  const markY = brandTop + (brandRowH - markSize) / 2
  ctx.fillStyle = c.markBg
  roundRect(ctx, PAD_X, markY, markSize, markSize, 16)
  ctx.fill()
  ctx.strokeStyle = c.markInner
  ctx.lineWidth = 4
  roundRect(ctx, PAD_X + 19, markY + 19, markSize - 38, markSize - 38, 5)
  ctx.stroke()

  const textX = PAD_X + markSize + 22
  ctx.textAlign = 'left'
  ctx.textBaseline = 'top'
  ctx.fillStyle = c.wordmark
  ctx.font = font(800, 42)
  ctx.fillText(labels.brand, textX, brandTop)
  ctx.fillStyle = c.category
  ctx.font = font(500, 25)
  ctx.fillText(labels.category, textX, brandTop + 42 + 9)

  const brandBottom = brandTop + brandRowH

  // Footer — WHERE (quiet URL, never a sentence — must never repeat `label`)
  const footerFontSize = 28
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  ctx.font = font(600, footerFontSize)
  ctx.fillStyle = c.footer
  const footerY = HEIGHT - PAD_BOTTOM - footerFontSize
  ctx.fillText(labels.footer, WIDTH / 2, footerY)

  // Core — WHAT (vertically centered between brand block and footer)
  const coreTop = brandBottom
  const coreBottom = footerY - 24
  const availableHeight = Math.max(coreBottom - coreTop, 0)

  const eyebrowSize = 24
  const numwrapGapTop = 44
  const numwrapPadY = 26
  const numwrapPadX = 54
  const labelGapTop = 40
  const labelSize = 48
  const totalGapTop = 30
  const totalPadY = 16
  const totalPadX = 32
  const totalValueSize = 34
  const totalSuffixSize = 27
  const totalGap = 12

  // Digit-capacity: widest configured numeral (5-digit "10,000") must still clear the
  // numwrap's own horizontal padding within the card's content width.
  const maxNumeralWidth = WIDTH - PAD_X * 2 - numwrapPadX * 2
  const numeralText = labels.numeral
  const numeralSize = fitNumeralFontSize(ctx, numeralText, maxNumeralWidth)
  ctx.font = font(800, numeralSize)
  const numeralTextWidth = ctx.measureText(numeralText).width
  const numeralLineHeight = numeralSize * 0.84
  const numwrapW = numeralTextWidth + numwrapPadX * 2
  const numwrapH = numeralLineHeight + numwrapPadY * 2

  const labelLineHeight = labelSize * 1.1
  ctx.font = font(700, totalValueSize)
  const totalValueWidth = ctx.measureText(labels.totalValue).width
  ctx.font = font(500, totalSuffixSize)
  const totalSuffixWidth = ctx.measureText(labels.totalSuffix).width
  const totalContentH = Math.max(totalValueSize, totalSuffixSize)
  const totalH = totalContentH + totalPadY * 2

  const stackHeight =
    eyebrowSize + numwrapGapTop + numwrapH + labelGapTop + labelLineHeight + totalGapTop + totalH
  let cursorY = coreTop + Math.max((availableHeight - stackHeight) / 2, 0)

  // Eyebrow
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  ctx.font = font(700, eyebrowSize)
  ctx.fillStyle = c.eyebrow
  ctx.fillText(labels.eyebrow.toUpperCase(), WIDTH / 2, cursorY)
  cursorY += eyebrowSize + numwrapGapTop

  // Numeral + corner brackets
  const numwrapX = WIDTH / 2 - numwrapW / 2
  const numwrapY = cursorY
  const bracketSize = 66
  const bracketBorder = 6
  drawBracket(ctx, numwrapX, numwrapY, bracketSize, bracketBorder, 'tl', c.bracket)
  drawBracket(ctx, numwrapX + numwrapW - bracketSize, numwrapY, bracketSize, bracketBorder, 'tr', c.bracket)
  drawBracket(ctx, numwrapX, numwrapY + numwrapH - bracketSize, bracketSize, bracketBorder, 'bl', c.bracket)
  drawBracket(
    ctx, numwrapX + numwrapW - bracketSize, numwrapY + numwrapH - bracketSize,
    bracketSize, bracketBorder, 'br', c.bracket,
  )

  ctx.font = font(800, numeralSize)
  ctx.fillStyle = c.numeral
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(numeralText, WIDTH / 2, numwrapY + numwrapPadY + numeralLineHeight / 2)
  cursorY += numwrapH + labelGapTop

  // Label
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  ctx.font = font(500, labelSize)
  ctx.fillStyle = c.label
  ctx.fillText(labels.label, WIDTH / 2, cursorY)
  cursorY += labelLineHeight + totalGapTop

  // Total pill (real total, not the threshold)
  const pillW = totalValueWidth + totalSuffixWidth + totalGap + totalPadX * 2
  const pillX = WIDTH / 2 - pillW / 2
  const pillY = cursorY
  ctx.fillStyle = c.pillBg
  roundRect(ctx, pillX, pillY, pillW, totalH, totalH / 2)
  ctx.fill()

  const pillBaseline = pillY + totalPadY + totalContentH * 0.82
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  const pillTextStartX = pillX + totalPadX
  ctx.font = font(700, totalValueSize)
  ctx.fillStyle = c.pillValue
  ctx.fillText(labels.totalValue, pillTextStartX, pillBaseline)
  ctx.font = font(500, totalSuffixSize)
  ctx.fillStyle = c.pillSuffix
  ctx.fillText(labels.totalSuffix, pillTextStartX + totalValueWidth + totalGap, pillBaseline)

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('Failed to generate share image'))
    }, 'image/png')
  })
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number) {
  ctx.textAlign = 'left'
  const words = text.split(' ')
  let line = ''
  let cy = y
  for (const word of words) {
    const test = line ? `${line} ${word}` : word
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, cy)
      line = word
      cy += lineHeight
    } else {
      line = test
    }
  }
  if (line) ctx.fillText(line, x, cy)
}
